import { useState } from "react";
import { Button } from "@/components/ui/button";
import { formatDateTimeShort, formatMNOK } from "@/lib/format";
import type { LeadPayload } from "@/hooks/use-leads";
import { useLeadHotkeys } from "@/hooks/use-lead-hotkeys";
import { leadQueueReason } from "@/lib/lead-queue";
import { LEAD_OUTCOME_LABELS } from "@/lib/lead-stats-labels";
import { CallBackDialog, NotRelevantDialog, type NotRelevantInput } from "./LeadOutcomeDialogs";


interface LeadCardProps {
  payload: LeadPayload;
  pending?: boolean;
  onNoAnswer: (note: string | null) => void;
  /** VIB-3032: årsak og revurderingsdato følger med. */
  onNotRelevant: (input: NotRelevantInput) => void;
  onCallBack: (input: { callBackAt: string; note: string | null }) => void;
  /** VIB-3032: e-post er sendt, samme booker følger opp om en uke. */
  onEmailSent: () => void;
  /** VIB-3032: nummeret er feil, leadet settes til side til noen finner et nytt. */
  onWrongNumber: () => void;
  onSkip: () => void;
  /** CRM-4.4 kobler «Avtalt møte» (4) til bookingdialogen. */
  onBookMeeting?: () => void;
}

function Meta({ children }: { children: React.ReactNode }) {
  return <span className="text-sm text-muted-foreground">{children}</span>;
}

/**
 * Ett lead om gangen (CRM-4.1). Snarveier (VIB-3050): 1 Ikke svar, 2 Ring tilbake, 3 Sendt e-post, 4 Avtalt møte,
 * 5 Avvist, 6 Feil nummer, S Hopp over.
 */
export default function LeadCard({ payload, pending, onNoAnswer, onNotRelevant, onCallBack, onEmailSent, onWrongNumber, onSkip, onBookMeeting }: LeadCardProps) {
  const { lead, contacts, attempts, brreg, list_script } = payload;
  const [callBackOpen, setCallBackOpen] = useState(false);
  const [notRelevantOpen, setNotRelevantOpen] = useState(false);

  useLeadHotkeys({
    onNoAnswer: pending ? undefined : () => onNoAnswer(null),
    onNotRelevant: () => setNotRelevantOpen(true),
    onCallBack: () => setCallBackOpen(true),
    onBookMeeting,
    onEmailSent: pending ? undefined : onEmailSent,
    onWrongNumber: pending ? undefined : onWrongNumber,
    onSkip: pending ? undefined : onSkip,
  });

  const facts = [
    lead.industry_description,
    lead.municipality,
    lead.employees != null ? `${lead.employees} ansatte` : null,
    lead.revenue_nok != null ? `Omsetning ${formatMNOK(lead.revenue_nok)}` : null,
  ].filter(Boolean);

  return (
    <article aria-label={`Lead: ${lead.name}`} className="border-[1.5px] border-foreground bg-card p-6 space-y-5">
      <p className="border-l-2 border-foreground pl-3 text-sm">{leadQueueReason(payload)}</p>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">{lead.name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {facts.map((f) => (
              <Meta key={f}>{f}</Meta>
            ))}
            {lead.website && /^https?:\/\//i.test(lead.website) && (
              <a href={lead.website} target="_blank" rel="noreferrer" className="text-sm underline">
                {lead.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {lead.priority && <span className="border border-foreground px-2 py-0.5 text-xs font-medium">Prioritet {lead.priority}</span>}
          {lead.locked_until && <Meta>Ditt til {formatDateTimeShort(lead.locked_until)}</Meta>}
        </div>
      </header>

      {lead.pitch && (
        <section aria-label="Hvorfor relevant" className="space-y-1">
          <h3 className="text-sm font-medium">Hvorfor relevant</h3>
          <p className="text-sm">{lead.pitch}</p>
        </section>
      )}

      {(brreg.founded_on || brreg.leader) && (
        <section aria-label="Brønnøysund" className="border border-border p-3 text-sm">
          {brreg.founded_on && <div>Stiftet {brreg.founded_on}</div>}
          {brreg.leader && <div>Daglig leder: {brreg.leader}</div>}
        </section>
      )}

      <section aria-label="Kontaktpersoner" className="space-y-1.5">
        <h3 className="text-sm font-medium">
          Kontaktpersoner
          {lead.phone_status === "uncertain" && <span className="ml-2 font-normal text-muted-foreground">(usikkert nummer)</span>}
        </h3>
        {contacts.length === 0 ? (
          <Meta>Ingen kontaktperson registrert. Ring sentralbordet.</Meta>
        ) : (
          <ul className="space-y-1">
            {contacts.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 text-sm">
                <span className="font-medium">{c.full_name}</span>
                {c.title && <Meta>{c.title}</Meta>}
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="underline" aria-label={`Ring ${c.full_name}`}>
                    {c.phone}
                  </a>
                )}
                {c.email && <Meta>{c.email}</Meta>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {lead.notes && (
        <section aria-label="Notat" className="space-y-1">
          <h3 className="text-sm font-medium">Notat</h3>
          <p className="whitespace-pre-line text-sm text-muted-foreground">{lead.notes}</p>
        </section>
      )}

      {list_script && (
        <details className="border border-border p-3 text-sm">
          <summary className="cursor-pointer font-medium">Manus for listen</summary>
          <p className="mt-2 whitespace-pre-line">{list_script}</p>
        </details>
      )}

      {lead.evidence_quote && (
        <section aria-label="Bevis fra agent" className="space-y-1">
          <h3 className="text-sm font-medium">Hvorfor akkurat nå</h3>
          <blockquote className="border-l-2 border-foreground pl-3 text-sm italic">{lead.evidence_quote}</blockquote>
          {lead.evidence_url && /^https?:\/\//i.test(lead.evidence_url) && (
            <a href={lead.evidence_url} target="_blank" rel="noreferrer" className="text-sm underline">
              Kilde
            </a>
          )}
        </section>
      )}

      {attempts.length > 0 && (
        <section aria-label="Tidligere forsøk" className="space-y-1">
          <h3 className="text-sm font-medium">Tidligere forsøk</h3>
          <ul className="space-y-0.5 text-sm text-muted-foreground">
            {attempts.map((a) => (
              <li key={a.created_at}>
                {formatDateTimeShort(a.created_at)} · {LEAD_OUTCOME_LABELS[a.outcome] ?? a.outcome} · {a.user_name}
                {a.note ? ` · ${a.note}` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2" role="group" aria-label="Utfall">
        <Button variant="outline" disabled={pending} onClick={() => onNoAnswer(null)} aria-keyshortcuts="1">
          <kbd className="mr-2 text-xs opacity-60">1</kbd>Ikke svar
        </Button>
        <Button variant="outline" disabled={pending} onClick={() => setCallBackOpen(true)} aria-keyshortcuts="2">
          <kbd className="mr-2 text-xs opacity-60">2</kbd>Ring tilbake senere
        </Button>
        <Button variant="outline" disabled={pending} onClick={onEmailSent} aria-keyshortcuts="3">
          <kbd className="mr-2 text-xs opacity-60">3</kbd>Sendt e-post
        </Button>
        {onBookMeeting && (
          <Button disabled={pending} onClick={onBookMeeting} aria-keyshortcuts="4">
            <kbd className="mr-2 text-xs opacity-60">4</kbd>Avtalt møte
          </Button>
        )}
        <Button variant="destructive" disabled={pending} onClick={() => setNotRelevantOpen(true)} aria-keyshortcuts="5">
          <kbd className="mr-2 text-xs opacity-60">5</kbd>Avvist
        </Button>
        <Button variant="outline" disabled={pending} onClick={onWrongNumber} aria-keyshortcuts="6">
          <kbd className="mr-2 text-xs opacity-60">6</kbd>Feil nummer
        </Button>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" disabled={pending} onClick={onSkip} aria-keyshortcuts="s">
          <kbd className="mr-1 text-xs opacity-60">S</kbd>Hopp over
        </Button>
      </div>

      <CallBackDialog
        open={callBackOpen}
        onOpenChange={setCallBackOpen}
        pending={pending}
        onSubmit={({ callBackAt }) => {
          setCallBackOpen(false);
          onCallBack({ callBackAt, note: null });
        }}
      />
      <NotRelevantDialog
        open={notRelevantOpen}
        onOpenChange={setNotRelevantOpen}
        pending={pending}
        onSubmit={(input) => {
          setNotRelevantOpen(false);
          onNotRelevant(input);
        }}
      />
    </article>
  );
}
